import { useEffect, useRef, type ReactElement, type ReactNode } from 'react';

import { cn } from '@docuid/ui/lib/utils';

type RevealVariant = 'up' | 'scale' | 'left' | 'right';

interface RevealProperties {
  children: ReactNode;
  className?: string;
  variant?: RevealVariant;
  delay?: number;
}

const variantClass: Record<RevealVariant, string> = {
  up: 'reveal',
  scale: 'reveal reveal-scale',
  left: 'reveal reveal-left',
  right: 'reveal reveal-right',
};

export function Reveal({
  children,
  className,
  variant = 'up',
  delay = 0,
}: RevealProperties): ReactElement {
  const reference = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = reference.current;
    if (!element) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      element.classList.add('reveal-in-view');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('reveal-in-view');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -48px 0px' },
    );

    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={reference}
      className={cn(variantClass[variant], className)}
      style={{ animationDelay: `${String(delay)}ms` }}
    >
      {children}
    </div>
  );
}
